import { useMemo } from 'react';

import { useDispositiveDiagramSyncContext } from '@/modules/dispositiveProvisions/contexts/DispositiveDiagramSync.context';
import { useDispositiveProvisionsContext } from '@/modules/dispositiveProvisions/contexts/dispositiveProvisions.context';
import { DispositionsFormSplitScreenRightPanel } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/panels/DispositionsFormSplitScreenRightPanel';
import { AfterDeath } from '@/types/schema';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import { DispositiveProvisionTileIcon } from '../DispositiveProvisionTileIcon';

export function DispositionsUnsupportedPanel() {
  const { primaryClients, isTwoClientHousehold } =
    useDispositiveProvisionsContext();
  const { selectedNode } = useDispositiveDiagramSyncContext();

  const subtitle = useMemo(() => {
    const isOrg =
      selectedNode &&
      getPulidKind(selectedNode.pulid) === PulidKind.ClientOrganization;

    const isIndividual =
      selectedNode &&
      getPulidKind(selectedNode.pulid) === PulidKind.ClientProfile;

    if (isOrg) {
      // Organizations never support dispositions
      return 'Luminary does not support dispositions for charitable organizations.';
    }

    if (
      isIndividual &&
      !primaryClients?.some((c) => c.id === selectedNode?.pulid)
    ) {
      // Non-client individuals never support dispositions
      return 'Luminary does not support dispositions for non-client individuals.';
    }

    const shouldShowUnsupportedPanelForFinalDeath = (() => {
      if (!selectedNode) return false;
      if (!isTwoClientHousehold) {
        return selectedNode.afterDeath === AfterDeath.First;
      }
      return selectedNode.afterDeath === AfterDeath.Second;
    })();

    if (shouldShowUnsupportedPanelForFinalDeath) {
      // Show the unsupported panel for final death
      return isTwoClientHousehold
        ? 'Luminary does not currently support adding dispositions for recipients after the second death.'
        : 'Luminary does not currently support adding dispositions that occur after the client’s death.';
    }

    return null;
  }, [isTwoClientHousehold, primaryClients, selectedNode]);

  if (!selectedNode || !subtitle || !primaryClients?.length) {
    return null;
  }

  return (
    <DispositionsFormSplitScreenRightPanel
      dataTestId="secondDeathUnsupportedPanel"
      IconElement={
        <DispositiveProvisionTileIcon
          size="large"
          nodeId={selectedNode.pulid}
        />
      }
      title={selectedNode.node?.data.lineOne}
      subtitle={subtitle}
    />
  );
}
